import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaUsers, FaCogs, FaChartLine, FaLightbulb, FaRocket, FaGraduationCap } from 'react-icons/fa';
import SEO from '../components/SEO';

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--secondary-50) 100%);
  padding: 120px 0 80px;
  margin-top: 70px;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 var(--space-md);
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: var(--space-lg);
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: var(--secondary-600);
  margin-bottom: var(--space-2xl);
  line-height: 1.6;
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(Link)`
  background: var(--primary-600);
  color: white;
  padding: var(--space-md) var(--space-2xl);
  border-radius: var(--radius-lg);
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  transition: all var(--transition-normal);

  &:hover {
    background: var(--primary-700);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: var(--primary-600);
  padding: var(--space-md) var(--space-2xl);
  border: 2px solid var(--primary-600);
  border-radius: var(--radius-lg);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--transition-normal);

  &:hover {
    background: var(--primary-600);
    color: white;
    transform: translateY(-2px);
  }
`;

const Section = styled.section`
  padding: 80px 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-md);
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: var(--space-lg);
  font-size: 2.5rem;
  color: var(--secondary-900);
`;

const SectionSubtitle = styled.p`
  text-align: center;
  margin-bottom: var(--space-3xl);
  font-size: 1.125rem;
  color: var(--secondary-600);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-2xl);
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  text-align: center;
  padding: var(--space-xl);
  background: white;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--secondary-200);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;

  h3 {
    margin: var(--space-md) 0;
    color: var(--secondary-900);
    text-align: center;
    transition: color 0.3s ease;
    min-height: 3.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .title-split {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    width: 100%;
    min-height: 3.5rem;
  }

  .title-line1 {
    display: block;
    width: 100%;
    text-align: center;
    line-height: 1.4;
  }

  .title-line2-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.25rem;
    position: relative;
    width: 100%;
    line-height: 1.4;
  }

  .title-line2 {
    display: inline-block;
    text-align: center;
    position: relative;
  }

  p {
    color: var(--secondary-600);
    line-height: 1.6;
    transition: color 0.3s ease;
    flex-grow: 1;
    margin: 0;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--primary-300);

    div:first-child {
      transform: scale(1.1) rotate(5deg);
    }
  }
`;

const FeatureIcon = styled(motion.div)`
  font-size: 2.5rem;
  color: var(--primary-600);
  margin-bottom: var(--space-md);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-2xl);
  align-items: stretch;
`;

const ServiceCard = styled(motion.div)`
  background: white;
  padding: var(--space-2xl);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--secondary-200);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-500), var(--primary-600));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--primary-300);

    &::before {
      transform: scaleX(1);
    }
  }

  h3 {
    color: var(--primary-600);
    margin-bottom: var(--space-md);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--space-sm);
    transition: color 0.3s ease;
    font-size: 1.5rem;
    font-weight: 600;
    min-height: 2.5rem;
    flex-shrink: 0;
  }

  .icon {
    font-size: 1.5rem;
    color: var(--primary-500);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover .icon {
    transform: scale(1.2) rotate(10deg);
  }

  p {
    color: var(--secondary-600);
    line-height: 1.6;
    margin-bottom: var(--space-lg);
    transition: color 0.3s ease;
    flex-shrink: 0;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    li {
      padding: var(--space-sm) 0;
      border-bottom: 1px solid var(--secondary-100);
      display: flex;
      align-items: center;
      transition: transform 0.2s ease;
      line-height: 1.5;
      
      &:last-child {
        border-bottom: none;
      }
      
      &:before {
        content: '✓';
        color: var(--success-600);
        font-weight: bold;
        margin-right: var(--space-sm);
        flex-shrink: 0;
        width: 1.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 1.25rem;
      }

      &:hover {
        transform: translateX(4px);
      }
    }
  }
`;

const TestimonialsSection = styled(Section)`
  background: var(--secondary-50);
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: var(--space-3xl);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const TestimonialContent = styled.blockquote`
  font-size: 1.25rem;
  font-style: italic;
  color: var(--secondary-700);
  margin-bottom: var(--space-xl);
  line-height: 1.6;
`;

const TestimonialAuthor = styled.div`
  h4 {
    color: var(--secondary-900);
    margin-bottom: var(--space-xs);
  }

  p {
    color: var(--secondary-600);
    font-size: 0.9rem;
  }
`;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);

      const handleChange = (e) => setPrefersReducedMotion(e.matches);
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const animationConfig = {
    duration: prefersReducedMotion ? 0 : 0.8,
    ease: [0.4, 0, 0.2, 1]
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const features = useMemo(() => [
    {
      icon: <FaUsers />,
      title: t('home.teamTransformation'),
      description: t('home.teamTransformationDesc')
    },
    {
      icon: <FaCogs />,
      title: t('home.processOptimization'),
      description: t('home.processOptimizationDesc')
    },
    {
      icon: <FaChartLine />,
      title: t('home.performanceMetrics'),
      description: t('home.performanceMetricsDesc')
    },
    {
      icon: <FaLightbulb />,
      title: t('home.innovationCulture'),
      description: t('home.innovationCultureDesc')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, i18n.language]);

  const featuredServices = useMemo(() => [
    {
      title: t('home.featuredService1.title'),
      icon: <FaRocket className="icon" />,
      shortDescription: t('home.featuredService1.shortDescription'),
      features: [
        t('home.featuredService1.feature1'),
        t('home.featuredService1.feature2'),
        t('home.featuredService1.feature3')
      ]
    },
    {
      title: t('home.featuredService2.title'),
      icon: <FaGraduationCap className="icon" />,
      shortDescription: t('home.featuredService2.shortDescription'),
      features: [
        t('home.featuredService2.feature1'),
        t('home.featuredService2.feature2'),
        t('home.featuredService2.feature3')
      ]
    },
    {
      title: t('home.featuredService3.title'),
      icon: <FaUsers className="icon" />,
      shortDescription: t('home.featuredService3.shortDescription'),
      features: [
        t('home.featuredService3.feature1'),
        t('home.featuredService3.feature2'),
        t('home.featuredService3.feature3')
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, i18n.language]);

  const featuredTestimonial = useMemo(() => ({
    content: t('home.featuredTestimonial.content'),
    name: t('home.featuredTestimonial.name'),
    position: t('home.featuredTestimonial.position'),
    company: t('home.featuredTestimonial.company')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, i18n.language]);

  // Organization structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "ScrumNX",
    "alternateName": "Professional Scrum Master & Agile Coach",
    "description": "Transform your team's productivity with expert guidance in Agile methodologies, Scrum frameworks, and organizational change.",
    "url": "https://scrumnx.com",
    "logo": "https://scrumnx.com/logo.png",
    "image": "https://scrumnx.com/og-image.jpg",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+49-17630574191",
      "contactType": "customer service",
      "email": "contact@scrumnx.com",
      "availableLanguage": ["English", "German"]
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "DE"
    },
    "sameAs": [
      "https://www.linkedin.com/company/ipm-consulting-or"
    ],
    "areaServed": "Worldwide",
    "serviceType": [
      "Agile Transformation",
      "Scrum Master Training",
      "Team Coaching",
      "Digital Transformation"
    ]
  };

  return (
    <>
      <SEO
        title="Home"
        description="Transform your team's productivity with expert guidance in Agile methodologies, Scrum frameworks, and organizational change. Professional Scrum Master and Agile Coach services."
        keywords="Scrum Master, Agile Coach, Agile Transformation, Scrum Training, Team Coaching, Digital Transformation, Product Management, Professional Services"
        schema={organizationSchema}
      />
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
            animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
            transition={animationConfig}
          >
            {t('home.title')}
          </HeroTitle>
          <HeroSubtitle
            initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
            animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
            transition={{ ...animationConfig, delay: prefersReducedMotion ? 0 : 0.2 }}
          >
            {t('home.subtitle')}
          </HeroSubtitle>
          <CTAButtons
            initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
            animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
            transition={{ ...animationConfig, delay: prefersReducedMotion ? 0 : 0.4 }}
          >
            <PrimaryButton to="/contact" onClick={scrollToTop}>
              {t('home.cta')} <FaArrowRight />
            </PrimaryButton>
            <SecondaryButton to="/services" onClick={scrollToTop}>
              {t('home.exploreServices')}
            </SecondaryButton>
          </CTAButtons>
        </HeroContent>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle>{t('home.whyChoose')}</SectionTitle>
          <SectionSubtitle>
            {t('home.whyChooseSubtitle')}
          </SectionSubtitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 30, scale: 0.9 }}
                whileInView={prefersReducedMotion ? false : { opacity: 1, y: 0, scale: 1 }}
                whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                transition={{
                  duration: prefersReducedMotion ? 0 : 0.6,
                  delay: prefersReducedMotion ? 0 : index * 0.1,
                  ease: [0.4, 0, 0.2, 1]
                }}
                viewport={{ once: true, margin: "-50px" }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <h3>
                  {feature.title.includes('-') ? (
                    <span className="title-split">
                      <span className="title-line1">{feature.title.split('-')[0]}</span>
                      <span className="title-line2-wrapper">
                        <span className="title-line2">-{feature.title.split('-').slice(1).join('-')}</span>
                      </span>
                    </span>
                  ) : (
                    feature.title
                  )}
                </h3>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </Section>

      <Section style={{ background: 'var(--secondary-50)' }}>
        <Container>
          <SectionTitle>{t('home.ourServices')}</SectionTitle>
          <SectionSubtitle>
            {t('home.ourServicesSubtitle')}
          </SectionSubtitle>
          <ServicesGrid>
            {featuredServices.map((service, index) => (
              <ServiceCard
                key={index}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 30, scale: 0.9 }}
                whileInView={prefersReducedMotion ? false : { opacity: 1, y: 0, scale: 1 }}
                whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                transition={{
                  duration: prefersReducedMotion ? 0 : 0.6,
                  delay: prefersReducedMotion ? 0 : index * 0.1,
                  ease: [0.4, 0, 0.2, 1]
                }}
                viewport={{ once: true, margin: "-50px" }}
              >
                <h3>
                  {service.icon}
                  {service.title}
                </h3>
                <p>{service.shortDescription}</p>
                <ul>
                  {service.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </ServiceCard>
            ))}
          </ServicesGrid>
          <div style={{ textAlign: 'center', marginTop: 'var(--space-2xl)' }}>
            <PrimaryButton to="/services" onClick={scrollToTop}>
              {t('home.viewAllServices')} <FaArrowRight />
            </PrimaryButton>
          </div>
        </Container>
      </Section>

      <TestimonialsSection>
        <Container>
          <SectionTitle>{t('home.whatClientsSay')}</SectionTitle>
          <SectionSubtitle>
            {t('home.whatClientsSaySubtitle')}
          </SectionSubtitle>
          <TestimonialCard
            initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
            whileInView={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.8 }}
            viewport={{ once: true }}
          >
            <TestimonialContent>"{featuredTestimonial.content}"</TestimonialContent>
            <TestimonialAuthor>
              <h4>{featuredTestimonial.name}</h4>
              <p>{featuredTestimonial.position} at {featuredTestimonial.company}</p>
            </TestimonialAuthor>
          </TestimonialCard>
        </Container>
      </TestimonialsSection>

      <Section>
        <Container>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <SectionTitle>{t('home.readyToTransform')}</SectionTitle>
            <SectionSubtitle>
              {t('home.readyToTransformSubtitle')}
            </SectionSubtitle>
            <CTAButtons>
              <PrimaryButton to="/contact" onClick={scrollToTop}>
                {t('home.scheduleConsultation')} <FaArrowRight />
              </PrimaryButton>
              <SecondaryButton to="/blog" onClick={scrollToTop}>
                {t('home.readOurBlog')}
              </SecondaryButton>
            </CTAButtons>
          </div>
        </Container>
      </Section>
    </>
  );
};

export default HomePage;